import React from 'react';
import { GatsbyImage } from 'gatsby-plugin-image';
import styled, { css } from 'styled-components';
import {
  brandColours,
  fontWeights,
  maxBreakpointQuery,
  minBreakpointQuery,
  sectionMargins,
  sectionPaddings,
  standardColours,
} from '../styles';
import { Button, Container, Heading, HtmlContent, Svg } from './ui';
import quotemarks from '../images/quotemarks.inline.svg';

const StyledImageContent = styled.section`
  ${({ isBlocks }) =>
    isBlocks ? sectionPaddings('30px', '60px') : sectionMargins()}
`;

const StyledInner = styled.div`
  --gap: 30px;
  display: grid;
  gap: var(--gap);

  ${minBreakpointQuery.small`
    --gap: 40px;
  `}

  ${minBreakpointQuery.smedium`
    grid-template-columns: ${({ flip }) =>
      flip ? 'calc(49% - var(--gap)) 51%' : '51% calc(49% - var(--gap))'};
    align-items: center;
  `}

  ${minBreakpointQuery.medium`
    --gap: 60px;
  `}

  ${minBreakpointQuery.large`
    --gap: 80px;
  `}

  ${minBreakpointQuery.xlarge`
    --gap: 100px;
  `}

  ${minBreakpointQuery.xxlarge`
    --gap: 120px;
  `}

  ${({ version }) => {
    if (version === 1) {
      return css`
        ${minBreakpointQuery.xxxlarge`
          --gap: 150px;
        `}

        ${minBreakpointQuery.xxxxlarge`
          --gap: 180px;
        `}
      `;
    } else if (version === 2) {
      return css`
        ${minBreakpointQuery.xxxlarge`
          --gap: 160px;
        `}

        ${minBreakpointQuery.xxxxlarge`
          --gap: 200px;
        `}
      `;
    }
  }}
`;

const StyledImageWrapper = styled.div`
  ${maxBreakpointQuery.smedium`
    aspect-ratio: 2/1;
  `}

  ${maxBreakpointQuery.small`
    aspect-ratio: 3/2;
  `}

  ${maxBreakpointQuery.tsmall`
    aspect-ratio: 4/3;
  `}

  ${minBreakpointQuery.smedium`
    aspect-ratio: 6/5;
  `}

  ${({ isIllustration }) => {
    if (isIllustration) {
      return css`
        text-align: center;
        background-color: ${brandColours.quinary};
      `;
    }
  }}
  
  ${({ flip }) => {
    if (flip) {
      return css`
        ${minBreakpointQuery.smedium`
          grid-row: 1;
          grid-column: 2;
        `}
      `;
    }
  }}

  ${({ version }) => {
    if (version === 1) {
      return css`
        border-radius: 10px;
        overflow: hidden;
      `;
    } else if (version === 2) {
      return css`
        ${({ flip }) => (flip ? 'margin-right: -30px;' : 'margin-left: -30px;')}

        ${maxBreakpointQuery.smedium`
          ${({ flip }) =>
            flip ? 'margin-left: -30px;' : 'margin-right: -30px;'}
        `}
      `;
    }
  }}
`;

const StyledImage = styled(GatsbyImage)`
  height: 100%;
`;

const StyledIllustrationImage = styled.img`
  padding: 30px;
  width: 100%;
  max-width: 80%;
  height: 100%;

  ${minBreakpointQuery.small`
    padding: 35px;
  `}

  ${minBreakpointQuery.large`
    padding: 40px;
  `}
`;

const StyledContent = styled.div`
  ${minBreakpointQuery.smedium`
    margin-top: 30px;
    margin-bottom: 30px;
  `}

  ${({ version }) => {
    if (version === 2) {
      return css`
        ${minBreakpointQuery.smedium`
          ${({ flip }) => (flip ? 'margin-left: auto;' : 'margin-right: auto;')}
          max-width: calc((1500px * 0.49) - var(--gap));
        `}
      `;
    }
  }}
`;

const StyledHeading = styled(Heading)`
  margin-bottom: 20px;

  ${minBreakpointQuery.small`
    margin-bottom: 25px;
  `}

  ${minBreakpointQuery.large`
    margin-bottom: 30px;
  `}
`;

const StyledText = styled(HtmlContent)``;

const StyledButton = styled(Button)`
  margin-top: 20px;

  ${minBreakpointQuery.small`
    margin-top: 25px;
  `}

  ${minBreakpointQuery.large`
    margin-top: 30px;
  `}
`;

const StyledTestimonial = styled.figure`
  background-color: ${standardColours.white};
  padding: 30px;
  border-radius: 40px;
  box-shadow: 0px 2px 4px ${standardColours.transparentBlack(0.1)};
  margin: 0;
  margin-top: 20px;
  position: relative;

  ${minBreakpointQuery.small`
    padding: 35px;
    margin-top: 30px;
  `}

  ${minBreakpointQuery.medium`
    padding: 40px;
    margin-top: 40px;
  `}

  ${minBreakpointQuery.large`
    padding: 45px;
    margin-top: 50px;
  `}

  ${minBreakpointQuery.xxlarge`
    padding: 50px;
    margin-top: 60px;
  `}
`;

const StyledQuotemarks = styled(Svg)`
  width: 28px;
  height: 20px;
  transform: scaleX(-1);
  fill: ${brandColours.quaternary};
  position: absolute;
  top: -10px;
  right: 30px;

  ${minBreakpointQuery.small`
    width: 42px;
    height: 30px;
    top: -15px;
    right: 42px;
  `}

  ${minBreakpointQuery.large`
    width: 56px;
    height: 40px;
    top: -20px;
    right: 56px;
  `}
`;

const StyledQuote = styled.blockquote`
  margin: 0;
`;

const StyledQuoteText = styled.p`
  line-height: 1.7;
`;

const StyledCite = styled.cite`
  display: block;
  margin-top: 20px;
  font-weight: ${fontWeights.semibold};
  font-style: normal;
  line-height: 1.7;
`;

const ImageContent = ({
  image,
  heading,
  text,
  link,
  testimonial,
  flip,
  version,
  isBlocks,
}) => (
  <StyledImageContent isBlocks={isBlocks}>
    <Container wide={version === 1} wider={version === 2} isBlocks={isBlocks}>
      <StyledInner flip={flip} version={version}>
        <StyledImageWrapper
          isIllustration={!image.gatsbyImageData}
          flip={flip}
          version={version}
        >
          {image.gatsbyImageData ? (
            <StyledImage image={image.gatsbyImageData} alt={image.alt} />
          ) : (
            <StyledIllustrationImage
              src={image.url}
              alt={image.alt}
              loading="lazy"
            />
          )}
        </StyledImageWrapper>
        <StyledContent flip={flip} version={version}>
          {heading && (
            <StyledHeading forwardedAs={isBlocks && 'h3'} alt={isBlocks}>
              {heading}
            </StyledHeading>
          )}
          <StyledText content={text} />
          {link && <StyledButton to={link.pageUrl}>{link.text}</StyledButton>}
          {testimonial && (
            <StyledTestimonial>
              <StyledQuotemarks image={quotemarks} />
              <StyledQuote>
                <StyledQuoteText>{testimonial.quote}</StyledQuoteText>
              </StyledQuote>
              <StyledCite>
                {`— ${testimonial.author}, ${testimonial.role}`}
              </StyledCite>
            </StyledTestimonial>
          )}
        </StyledContent>
      </StyledInner>
    </Container>
  </StyledImageContent>
);

export default ImageContent;
